import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Field, Form } from 'react-final-form';
import styled from 'styled-components';

import { useIsSignedIn, useOauthSignup, useSignup } from '@front/auth/src/authentication-context';

import useModal from '@front/common/src/hooks/use-modal';
import {
  addressValidator,
  compose,
  emailValidator,
  internationalPhoneValidator,
  required,
  tooShort,
} from '@front/common/src/forms/validations';
import { validateAddress } from '@front/common/src/forms/validators/address';
import { isChildcareRecurring as isChildcareRecurringUtil } from '@front/common/src/utils/basket';
import { toRem } from '@front/common/src/utils/styles';
import { Address, OAuthLoginData, SignupUser, User } from '@front/common/src/types/commons';
import { BasketState } from '@front/common/src/ducks/customer/types';
import { UserState } from '@front/common/src/ducks/common/types';
import { useUserEmailExists } from '@front/common/src/queries/user-queries';
import { getCurrentCountry } from '@front/common/src/utils/country';
import { useConfig } from '@front/common/src/providers/config-provider';
import { useVariant } from '@front/common/src/providers/experiment-provider';
import { CUSTOMER_AUTOCOMPLETE_EXCLUDED_TYPES } from '@front/common/src/constants/woosmap';
import { getBasketWeeklyRecurrence } from '@front/common/src/ducks/customer/basket-selectors';
import { isCleaningReference } from '@front/common/src/utils/universes';

import { getScreenTitle as getScreenTitleForChildcareAndCleaning } from '@front/customer/src/screens/signup-choice/utils';

import { translate } from '@front/i18n';

import WoosmapAutocompleteLegacy from '@front/web-ui/src/components/autocomplete-address/woosmap-autocomplete-legacy';
import Text from '@front/web-ui/src/components/base-text/variants/text';
import InputPhoneLegacy from '@front/web-ui/src/components/input-phone/input-phone-legacy';
import Button from '@front/web-ui/src/components/button/button';
import { updatePhone, updateAddress, UpdatePhoneMutateValues } from '@front/web-ui/src/forms/mutators';
import { InputAdapter } from '@front/web-ui/src/forms/adapters';
import GenderSelector from '@front/web-ui/src/components/gender-selector/gender-selector';
import Screen from '@front/web-ui/src/components/screen/screen';
import Alert from '@front/web-ui/src/components/alert/alert';
import { ValidatedPasswordLegacy } from '@front/web-ui/src/components/input-password/validated-password-legacy';

import { RootState, useAppSelector } from '../../store';
import { logWoosmapErrorIncompleteAddress, trackSignInEvent } from '../../utils/tracking';
import appConfig from '../../utils/config';

import TermsOfService from './terms-of-service';
import EmailTakenAlertModal from './common/email-taken-alert-modal';
import { Container, ErrorContainer, StyledForm } from './common/styles';
import AlreadySubscribed from './already-subscribed';
import { firstNameValidator, lastNameValidator } from './common/names-inputs';
import useSignupNavigation from './use-signup-navigation';

const t = translate('screens.signup');

const TextContainer = styled.div`
  margin-bottom: ${toRem(20)};
`;

type GetScreenTitleArgs = {
  isChildcareRecurring: boolean;
  showAllFields: boolean;
  variantForTitle?: string;
  withSocialLogin: boolean;
};

const getScreenTitle = ({
  isChildcareRecurring,
  showAllFields,
  variantForTitle,
  withSocialLogin,
}: GetScreenTitleArgs) => {
  if (withSocialLogin) {
    return t(
      `.titles.${showAllFields ? 'oauth_information_to_check_and_complete' : 'oauth_information_to_complete'}`,
    );
  }
  return getScreenTitleForChildcareAndCleaning({ variantForTitle, isChildcareRecurring });
};

const userEmailValidator = compose(required('email'), emailValidator('email'));

type FormValues = Partial<Address> & UserState['thirdPartyAuth']['partialUser'] & UpdatePhoneMutateValues;

const validate = (values: FormValues) => ({
  address: addressValidator('address')(values),
  phone: internationalPhoneValidator('phone', values.isPhoneValid)(values.phoneFormatted),
});

type StateProps = {
  basketAddress: BasketState['address'];
  universe: BasketState['universe'];
  isChildcareRecurring: boolean;
};

type SignupProps = StateProps;

type OnSubmitParams = UpdatePhoneMutateValues & SignupUser;

export const Signup = ({ basketAddress, universe, isChildcareRecurring }: SignupProps) => {
  const isSignedIn = useIsSignedIn();
  const location = useLocation();
  const config = useConfig();
  const { partialUser, omniAuthData }: { partialUser?: Partial<User>; omniAuthData?: OAuthLoginData } =
    location.state || {};
  const withSocialLogin = !!partialUser && !!omniAuthData;
  const { mutate: checkEmailTaken, isLoading: emailCheckIsLoading } = useUserEmailExists();
  const { onAccountCreated, openLoginScreen } = useSignupNavigation(isSignedIn);
  const { signup, signupError, isSignupLoading } = useSignup({
    onSuccess: onAccountCreated,
  });
  const { oauthSignupError, oauthSignup, isOauthSignupLoading } = useOauthSignup({
    onSuccess: onAccountCreated,
  });
  const weeklyRecurrence = useAppSelector(getBasketWeeklyRecurrence);

  const showAllFields =
    !partialUser ||
    !!userEmailValidator(partialUser.email) ||
    !!firstNameValidator(partialUser.first_name) ||
    !!lastNameValidator(partialUser.last_name);

  const [addressError, setAddressError] = useState<string>();
  const [emailTakenModalVisible, openEmailTakenModal, closeEmailTakenModal] = useModal();
  // We must set partial user before address otherwise partial user will override address values
  const [initialValues] = useState({
    ...(partialUser || {}),
    ...(validateAddress(basketAddress) ? basketAddress : {}),
  });

  const variantForTitle = useVariant('cst-cleaning-fr-signup-new-title');

  const onSubmit = ({
    phoneFormatted: phone,
    salutation,
    first_name,
    last_name,
    email,
    password,
    address,
    lat,
    lng,
    street_name,
    street_number,
    zip_code,
    city,
  }: OnSubmitParams) => {
    trackSignInEvent({
      eventType: 'CreateAccountButtonClick',
      path: location.pathname,
      universe,
    });
    const userData: SignupUser & {
      cleaning_frequency_at_registration?: number;
    } = {
      salutation,
      first_name,
      last_name,
      phone,
      email,
      password,
      address,
      city,
      lat,
      lng,
      street_name,
      street_number,
      zip_code,
      universe_creation: universe,
      country_code: getCurrentCountry(),
    };

    // the user can signup on the location selection screen and in this case the weeklyRecurrence is null
    if (isCleaningReference(universe) && Number.isFinite(weeklyRecurrence)) {
      userData.cleaning_frequency_at_registration = weeklyRecurrence!;
    }

    checkEmailTaken(email, {
      onSuccess: (isEmailTaken) => {
        if (isEmailTaken) {
          openEmailTakenModal();
        } else if (withSocialLogin) {
          oauthSignup({ omniAuthData, userData });
        } else {
          signup(userData);
        }
      },
    });
  };

  return (
    <Screen
      title={getScreenTitle({
        isChildcareRecurring,
        showAllFields,
        variantForTitle: variantForTitle?.value,
        withSocialLogin,
      })}
    >
      <EmailTakenAlertModal
        closeModal={closeEmailTakenModal}
        isOpen={emailTakenModalVisible}
        openLoginScreen={openLoginScreen}
      />
      <Container>
        {isChildcareRecurring && !withSocialLogin && (
          <TextContainer>
            <Text>{t('.subtitle_childcare')}</Text>
          </TextContainer>
        )}
        <ErrorContainer>
          {signupError && <Alert type='error' content={signupError} />}
          {oauthSignupError && <Alert type='error' content={oauthSignupError} />}
          {addressError && <Alert type='error' content={addressError} />}
        </ErrorContainer>
        <Form
          onSubmit={onSubmit}
          validate={validate}
          initialValues={initialValues}
          mutators={{ updatePhone, updateAddress }}
        >
          {({ handleSubmit, form, invalid, values }) => (
            <StyledForm onSubmit={handleSubmit} id='form'>
              <Field<string> name='salutation' validate={required('salutation')}>
                {({ input }) => <GenderSelector value={input.value} onChange={input.onChange} />}
              </Field>
              {showAllFields && (
                <Field
                  component={InputAdapter}
                  name='first_name'
                  type='text'
                  label={t('inputs.first_name.placeholder')}
                  id='formSignupFirstName'
                  autoComplete='given-name'
                  validate={firstNameValidator}
                  showsErrorsOnMount={withSocialLogin}
                />
              )}
              {showAllFields && (
                <Field
                  component={InputAdapter}
                  name='last_name'
                  type='text'
                  label={t('inputs.last_name.placeholder')}
                  id='formSignupLastName'
                  autoComplete='family-name'
                  validate={lastNameValidator}
                  showsErrorsOnMount={withSocialLogin}
                />
              )}
              <Field name='phone'>
                {({ input, meta }) => (
                  <InputPhoneLegacy
                    name='phone'
                    label={t('inputs.phone.placeholder')}
                    id='phone'
                    autoComplete='tel'
                    message={isChildcareRecurring ? '' : t('.address.phone_hint')}
                    onChange={form.mutators.updatePhone}
                    value={input.value}
                    onBlur={input.onBlur}
                    onFocus={input.onFocus}
                    error={meta.touched && meta.error ? meta.error : undefined}
                  />
                )}
              </Field>
              {!validateAddress(basketAddress) && (
                <WoosmapAutocompleteLegacy
                  label={t('inputs.address.label')}
                  formattedAddress={values.address}
                  excludedTypes={CUSTOMER_AUTOCOMPLETE_EXCLUDED_TYPES}
                  setAddressError={setAddressError}
                  setAddressFields={form.mutators.updateAddress}
                  apiKey={appConfig.WOOSMAP_API_KEY}
                  onIncompleteAddress={() => logWoosmapErrorIncompleteAddress()}
                />
              )}
              {showAllFields && (
                <Field
                  component={InputAdapter}
                  name='email'
                  type='email'
                  label={t('inputs.email.placeholder')}
                  id='formSignupEmail'
                  autoComplete='email'
                  validate={userEmailValidator}
                  showsErrorsOnMount={withSocialLogin}
                />
              )}
              {!withSocialLogin && (
                <Field
                  component={ValidatedPasswordLegacy}
                  name='password'
                  label={t('inputs.password.placeholder')}
                  id='formSignUpPassword'
                  autoComplete='password'
                  validate={compose(required('password'), tooShort('password', 7))}
                />
              )}
              <TermsOfService routes={config.routes} universe={universe} />
              <Button
                type='submit'
                size='lg'
                isLoading={isOauthSignupLoading || isSignupLoading || emailCheckIsLoading}
                isDisabled={invalid}
                hasFullWidth={true}
              >
                {t('.submit')}
              </Button>
              <AlreadySubscribed universe={universe} openLoginScreen={openLoginScreen} />
            </StyledForm>
          )}
        </Form>
      </Container>
    </Screen>
  );
};

export const mapStateToProps = (state: RootState): StateProps => ({
  basketAddress: state.basket.address,
  universe: state.basket.universe,
  isChildcareRecurring: isChildcareRecurringUtil(state.basket.basketContent),
});

export default connect(mapStateToProps, {})(Signup);
